import { useCallback, useEffect } from 'react';

import { ConceptGroupsConfidence } from '@verantos-dev/types-concepts';

import { useResearchAssistantSocketContext } from '../../contexts/ResearchAssistantSocketContext';

type ConceptGroupConfidenceMessage = (
  | string
  | {
      eventName: string;
      conceptGroups: {
        span: string;
        status: 'success' | 'warning' | 'error';
      }[];
    }
)[];

export const useConceptGroupConfidenceListeners = (
  onConceptGroupConfidence?: (confidence: ConceptGroupsConfidence) => void,
  eventNameToId: Record<string, string> = {}
) => {
  const { socket: currentSocket } = useResearchAssistantSocketContext();

  const transformMessage = useCallback(
    (message: ConceptGroupConfidenceMessage) => {
      const transformed: ConceptGroupsConfidence = message.map((item) => {
        const parsedItem = typeof item === 'string' ? JSON.parse(item) : item;
        return {
          eventId: eventNameToId[parsedItem.eventName] || parsedItem.eventName,
          eventName: parsedItem.eventName,
          conceptGroups: parsedItem.conceptGroups?.map((cg: { status: string; span: string }) => ({
            status: cg.status,
            span: cg.span,
          })),
        };
      });

      return transformed;
    },
    [eventNameToId]
  );

  const conceptGroupConfidenceHandler = useCallback(
    (message: string | ConceptGroupConfidenceMessage) => {
      if (!message) return;

      let parsedMessage: ConceptGroupConfidenceMessage;
      if (typeof message === 'string') {
        parsedMessage = JSON.parse(message) as ConceptGroupConfidenceMessage;
      } else {
        parsedMessage = message as ConceptGroupConfidenceMessage;
      }
      const transformed = transformMessage(parsedMessage);
      onConceptGroupConfidence?.(transformed);
    },
    [transformMessage, onConceptGroupConfidence]
  );

  useEffect(() => {
    if (currentSocket) {
      currentSocket.on('events_with_warnings', conceptGroupConfidenceHandler);
    }

    return () => {
      if (currentSocket) {
        currentSocket.off('events_with_warnings', conceptGroupConfidenceHandler);
      }
    };
  }, [conceptGroupConfidenceHandler, currentSocket]);
};
